import React, { useState, useRef } from 'react';
import { Box, Popper, IconButton, Tooltip, Zoom, ClickAwayListener, Fade } from '@mui/material';
import { QUICK_REACTIONS, emojiToType } from '../utils/reactions';

const EmojiReactionPicker = ({ 
  open, 
  anchorEl, 
  onClose, 
  onReactionSelect,
  selectedReactions = []
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const closeTimeoutRef = useRef(null);

  const handleReactionClick = (reaction) => {
    onReactionSelect(reaction);
    onClose();
  };

  const isReactionSelected = (reactionType) => {
    return selectedReactions.some((r) => {
      const type = r?.type || emojiToType(r?.emoji) || r?.emoji;
      return type === reactionType;
    });
  };

  const handleMouseEnter = () => {
    // Cancel any pending close
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    // Delay close slightly to allow moving between trigger and picker
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
    }, 150);
  };

  if (!open) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top"
      transition
      style={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <Box
            data-reaction-picker="true"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              borderRadius: 50,
              px: 1,
              py: 0.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              background: 'white',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', overflow: 'visible', height: 50 }}>
              {QUICK_REACTIONS.map((reaction, index) => (
                <Tooltip 
                  key={reaction.type} 
                  title={reaction.label}
                  TransitionComponent={Zoom}
                  arrow
                >
                  <IconButton
                    onClick={() => handleReactionClick(reaction)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(-1)}
                    sx={{
                      width: 38,
                      height: 38,
                      transition: 'all 0.18s ease-in-out',
                      transform: hoveredIndex === index ? 'translateY(-6px) scale(1.12)' : 'translateY(0px) scale(1)',
                      p: 0.5,
                      border: isReactionSelected(reaction.type) ? '2px solid' : '1px solid',
                      borderColor: isReactionSelected(reaction.type) ? 'primary.main' : 'divider',
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    aria-label={reaction.label}
                  >
                    <Box
                      component="img"
                      src={reaction.image}
                      alt={reaction.label}
                      sx={{
                        width: 22,
                        height: 22,
                        display: 'block',
                        userSelect: 'none',
                        pointerEvents: 'none',
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Box>
        </Fade>
      )}
    </Popper>
  );
};

export default EmojiReactionPicker;
